import Phaser from "phaser";
import ScoreManager from "../game/ScoreManager";
import DailyChallengeManager from "../game/DailyChallengeManager";
import XAuth from "../game/XAuth";
import { addMuteButton } from "../game/MuteButton";
import { FONT_SIZE, fontStyle } from "../game/FontScale";
import { fitText } from "../game/view/layout";

// Minimum clear space between rows / columns (px)
const GAP = 8;
const ROW_H = 22;
const MAX_VISIBLE = 18;
// Half the width of a 3-digit stage at BODY size ("100" = 36px)
const STAGE_HALF_W = 18;

const TABS = [
  { key: "DAILY", label: "DAILY" },
  { key: "EPOCH", label: "EPOCH" },
  { key: "ALL_TIME", label: "ALL TIME" },
];

class HighScoreScene extends Phaser.Scene {
  constructor() {
    super({ key: "HighScoreScene" });
  }

  create() {
    this.cameras.main.setBackgroundColor("#0a0a0a");

    const W = this.scale.width;
    const H = this.scale.height;
    const CX = W / 2;
    this._W = W;
    this._H = H;
    this._CX = CX;
    const mob = W < 600;

    // Columns scale with width: RANK / PLAYER left-aligned with a gap,
    // STAGE centred, WOOD right-aligned (at W - 16 on mobile).
    const scoreRight = mob ? W - 16 : W * 0.875;
    this._cols = {
      rankX: W * (mob ? 0.04 : 0.125),
      playerX: W * (mob ? 0.18 : 0.2125),
      stageX: scoreRight - (mob ? 140 : W * 0.2),
      scoreRight,
    };

    const xUser = XAuth.getUser();
    this.myHandle = xUser ? xUser.handle : null;
    this.activeTab = "EPOCH";
    this.scoreElements = [];

    // ── Header ──────────────────────────────────────────────────
    this.add
      .text(
        CX,
        28,
        "HIGH SCORES",
        fontStyle(FONT_SIZE.TITLE, "#FFD700", {
          stroke: "#8B6914",
          strokeThickness: 3,
        }),
      )
      .setOrigin(0.5);

    // ── Tabs ────────────────────────────────────────────────────
    this.tabTexts = [];
    const tabY = 60;
    const tabSpacing = W * 0.25;
    const tabStartX = CX - ((TABS.length - 1) * tabSpacing) / 2;

    TABS.forEach((tab, i) => {
      const x = tabStartX + i * tabSpacing;
      const text = this.add
        .text(
          x,
          tabY,
          tab.label,
          fontStyle(
            FONT_SIZE.BODY,
            tab.key === this.activeTab ? "#FFD700" : "#999999",
          ),
        )
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });

      text.on("pointerdown", () => this._switchTab(tab.key));
      text.on("pointerover", () => {
        if (tab.key !== this.activeTab) text.setStyle({ fill: "#BBBBBB" });
      });
      text.on("pointerout", () => {
        text.setStyle({
          fill: tab.key === this.activeTab ? "#FFD700" : "#999999",
        });
      });

      this.tabTexts.push({ key: tab.key, text });
    });

    // Tab underline
    this.tabUnderline = this.add.graphics();
    this._drawTabUnderline();

    // ── Column headers ──────────────────────────────────────────
    const { rankX, playerX, stageX } = this._cols;
    const colY = 82;
    this.add.text(rankX, colY, "RANK", fontStyle(FONT_SIZE.CAPTION, "#999999"));
    this.add.text(playerX, colY, "PLAYER", fontStyle(FONT_SIZE.CAPTION, "#999999"));
    this.add
      .text(stageX, colY, "STAGE", fontStyle(FONT_SIZE.CAPTION, "#999999"))
      .setOrigin(0.5, 0);
    this.add
      .text(scoreRight, colY, "WOOD", fontStyle(FONT_SIZE.CAPTION, "#999999"))
      .setOrigin(1, 0);

    const lineGfx = this.add.graphics();
    lineGfx.lineStyle(1, 0xffd700, 0.2);
    lineGfx.lineBetween(rankX - 8, colY + 12, scoreRight + 8, colY + 12);

    this.scoreStartY = colY + 20;

    // ── Loading indicator ───────────────────────────────────────
    this.loadingText = this.add
      .text(
        CX,
        H / 2,
        "LOADING...",
        fontStyle(FONT_SIZE.SUBHEADING, "#777777"),
      )
      .setOrigin(0.5);

    // ── Footer buttons ─────────────────────────────────────────
    // Desktop: side by side at W*0.28 / W*0.72. Mobile: stacked, BACK at
    // the bottom. The footer is laid out bottom-up from H.
    const btnBottom = H - (mob ? 24 : 20);
    const docsBtn = this.add
      .text(
        mob ? CX : W * 0.28,
        btnBottom,
        "HOW  IT  WORKS",
        fontStyle(FONT_SIZE.BODY, "#FFD700"),
      )
      .setOrigin(0.5, 1)
      .setInteractive({ useHandCursor: true });
    docsBtn.on("pointerdown", () => this.scene.start("DocsScene"));

    const backBtn = this.add
      .text(
        mob ? CX : W * 0.72,
        btnBottom,
        "BACK  TO  MENU",
        fontStyle(FONT_SIZE.BODY, "#00FFFF"),
      )
      .setOrigin(0.5, 1)
      .setInteractive({ useHandCursor: true });
    backBtn.on("pointerdown", () => this.scene.start("MenuScene"));
    fitText(docsBtn, mob ? W - 16 : W * 0.44 - GAP);
    fitText(backBtn, mob ? W - 16 : W * 0.44 - GAP);

    // Stack HOW IT WORKS above BACK on narrow screens (extra gap for taps)
    if (mob) docsBtn.setY(btnBottom - backBtn.height - GAP * 2);
    let footerTop = docsBtn.y - docsBtn.height;

    // ── Your stats (if signed in) — own line above the buttons ──
    if (this.myHandle) {
      const handleY = footerTop - GAP;
      const dotX = mob ? 16 + 4 : W * 0.12;
      const handleText = this.add
        .text(
          dotX + 10,
          handleY,
          this.myHandle,
          fontStyle(FONT_SIZE.CAPTION, "#39FF14"),
        )
        .setOrigin(0, 1);
      fitText(handleText, W - 16 - (dotX + 10));
      this.add
        .graphics()
        .fillStyle(0x39ff14)
        .fillCircle(dotX, handleY - handleText.height / 2, 4);
      footerTop = handleY - handleText.height;
    }

    // Rows (plus the proximity line) must end GAP above the footer
    this.scoresBottom = footerTop - GAP;
    this.input.keyboard.on("keydown-ESC", () =>
      this.scene.start("MenuScene"),
    );

    addMuteButton(this);

    // Load initial tab
    this._loadTab(this.activeTab);
  }

  _switchTab(tabKey) {
    if (tabKey === this.activeTab) return;
    this.activeTab = tabKey;

    // Update tab styles
    this.tabTexts.forEach((t) => {
      t.text.setStyle({
        fill: t.key === this.activeTab ? "#FFD700" : "#999999",
      });
    });
    this._drawTabUnderline();

    // Clear existing scores
    this._clearScores();

    // Show loading
    if (this.loadingText) this.loadingText.setVisible(true);

    this._loadTab(tabKey);
  }

  _drawTabUnderline() {
    this.tabUnderline.clear();
    const activeTabObj = this.tabTexts.find((t) => t.key === this.activeTab);
    if (activeTabObj) {
      const bounds = activeTabObj.text.getBounds();
      this.tabUnderline.lineStyle(2, 0xffd700, 1);
      this.tabUnderline.lineBetween(
        bounds.x,
        bounds.y + bounds.height + 2,
        bounds.x + bounds.width,
        bounds.y + bounds.height + 2,
      );
    }
  }

  _clearScores() {
    this.scoreElements.forEach((el) => el.destroy());
    this.scoreElements = [];
  }

  async _loadTab(tabKey) {
    try {
      let scores = [];

      if (tabKey === "DAILY") {
        scores = await this._fetchDailyScores();
      } else if (tabKey === "EPOCH") {
        scores = await this._fetchEpochScores();
      } else {
        scores = await this._fetchAllTimeScores();
      }

      if (this.loadingText) this.loadingText.setVisible(false);
      this._renderScores(scores, tabKey);
    } catch {
      if (this.loadingText) {
        this.loadingText.setVisible(true);
        this.loadingText.setText("FAILED TO LOAD");
      }
    }
  }

  async _fetchDailyScores() {
    const data = await DailyChallengeManager.fetchLeaderboard(undefined, 20);
    return (data.leaderboard || []).map((entry, i) => ({
      rank: i + 1,
      handle: entry.handle,
      score: entry.score,
      level: "-",
      isDaily: true,
    }));
  }

  async _fetchEpochScores() {
    try {
      const resp = await fetch("/api/leaderboard?limit=20&type=epoch");
      if (resp.ok) {
        const data = await resp.json();
        if (data.leaderboard && data.leaderboard.length > 0) {
          return data.leaderboard.map((entry, i) => ({
            rank: i + 1,
            handle: entry.handle,
            score: entry.score,
            level: entry.level || "-",
          }));
        }
      }
    } catch {
      // Fall through
    }
    // Fall back to all-time if no epoch endpoint
    return this._fetchAllTimeScores();
  }

  async _fetchAllTimeScores() {
    const scores = await ScoreManager.fetchLeaderboard(20);
    return (scores || []).map((entry, i) => ({
      rank: i + 1,
      handle: entry.handle,
      score: entry.score,
      level: entry.level || "-",
    }));
  }

  _renderScores(scores, tabKey) {
    this._clearScores();

    const W = this._W;
    const CX = this._CX;

    if (!scores || scores.length === 0) {
      const empty = this.add
        .text(
          CX,
          this._H / 2,
          "NO SCORES YET",
          fontStyle(FONT_SIZE.SUBHEADING, "#777777"),
        )
        .setOrigin(0.5);
      this.scoreElements.push(empty);
      return;
    }

    const rowH = ROW_H;
    const { rankX, playerX, stageX, scoreRight } = this._cols;
    // Leave room for the proximity line below the rows, and one extra row
    // for the "· · ·" separator in neighbourhood mode.
    const maxVisible = Phaser.Math.Clamp(
      Math.floor((this.scoresBottom - this.scoreStartY - 18) / rowH) - 1,
      6,
      MAX_VISIBLE,
    );
    const topCount = maxVisible - 4;

    // Find player's position
    const myIndex = this.myHandle
      ? scores.findIndex((s) => s.handle === this.myHandle)
      : -1;

    // If player is outside visible range, show neighborhood
    let displayScores = scores.slice(0, maxVisible);
    let showingNeighborhood = false;

    if (myIndex >= maxVisible && this.myHandle) {
      // Show top N + separator + 2 above me + me + 1 below
      const topSlice = scores.slice(0, topCount);
      const neighborStart = Math.max(topCount, myIndex - 2);
      const neighborEnd = Math.min(scores.length, myIndex + 2);
      const neighborhood = scores.slice(neighborStart, neighborEnd);
      displayScores = [...topSlice, null, ...neighborhood]; // null = separator
      showingNeighborhood = true;
    }

    displayScores.forEach((entry, i) => {
      const y = this.scoreStartY + i * rowH;

      // Separator row
      if (entry === null) {
        const sep = this.add
          .text(CX, y, "· · ·", fontStyle(FONT_SIZE.BODY, "#777777"))
          .setOrigin(0.5, 0);
        this.scoreElements.push(sep);
        return;
      }

      const rank = entry.rank;
      const isMe = this.myHandle && entry.handle === this.myHandle;
      const handle = (entry.handle || "ANON").substring(0, 16);

      // Highlight current user's row
      if (isMe) {
        const hl = this.add.graphics();
        hl.fillStyle(0x39ff14, 0.08);
        hl.fillRect(rankX - 8, y - 2, scoreRight - rankX + 16, rowH);
        this.scoreElements.push(hl);
      }

      // Rank — medal colors for top 3
      let rankColor = "#999999";
      let rankText = `${rank}.`;
      if (rank === 1) {
        rankColor = "#FFD700";
        rankText = "1ST";
      } else if (rank === 2) {
        rankColor = "#C0C0C0";
        rankText = "2ND";
      } else if (rank === 3) {
        rankColor = "#CD7F32";
        rankText = "3RD";
      }

      const rankEl = this.add.text(
        rankX,
        y,
        rankText,
        fontStyle(FONT_SIZE.BODY, rankColor),
      );
      this.scoreElements.push(rankEl);

      // Score (right-aligned)
      const scoreEl = this.add
        .text(
          scoreRight,
          y,
          `${(entry.score || 0).toLocaleString()}`,
          fontStyle(FONT_SIZE.BODY, isMe ? "#FFD700" : "#F5E6C8"),
        )
        .setOrigin(1, 0);
      this.scoreElements.push(scoreEl);

      // Player — fitted to end GAP before the STAGE (or WOOD) column
      const handleEl = this.add.text(
        playerX,
        y,
        handle,
        fontStyle(FONT_SIZE.BODY, isMe ? "#39FF14" : "#D4884A"),
      );
      const playerRight = entry.isDaily
        ? scoreRight - scoreEl.width - GAP
        : stageX - STAGE_HALF_W - GAP;
      fitText(handleEl, playerRight - playerX);
      this.scoreElements.push(handleEl);

      // Stage (hidden for daily tab)
      if (!entry.isDaily) {
        const stageEl = this.add
          .text(
            stageX,
            y,
            `${entry.level}`,
            fontStyle(FONT_SIZE.BODY, isMe ? "#39FF14" : "#888888"),
          )
          .setOrigin(0.5, 0);
        this.scoreElements.push(stageEl);
      }
    });

    // Proximity indicator — show distance to next rank above
    if (myIndex > 0 && this.myHandle) {
      const myScore = scores[myIndex].score;
      const aboveScore = scores[myIndex - 1].score;
      const gap = aboveScore - myScore;

      if (gap > 0) {
        const proxyY = showingNeighborhood
          ? this.scoreStartY + displayScores.length * rowH + 8
          : this.scoreStartY + Math.min(scores.length, maxVisible) * rowH + 8;

        const proxyText = this.add
          .text(
            CX,
            proxyY,
            `${gap.toLocaleString()} WOOD TO OVERTAKE #${myIndex}`,
            fontStyle(FONT_SIZE.CAPTION, "#FF00FF"),
          )
          .setOrigin(0.5);
        fitText(proxyText, W - 16);
        this.scoreElements.push(proxyText);

        // Pulse animation
        this.tweens.add({
          targets: proxyText,
          alpha: { from: 1, to: 0.4 },
          duration: 1200,
          yoyo: true,
          repeat: -1,
        });
      }
    }
  }
}

export default HighScoreScene;
